import { ColumnType, DataCell, TableColumn } from '@ecosystem/export-api';
import { StackedChartResp } from '@data-visualization/stacked-chart/Types';
import { getKpiDisplayName } from '../ExportUtils';
import { ExcelFormatter, Fact, formatKpiForExport, Kpi } from '@ecosystem/formatters';
import { FactRevenue } from '@utils/pageConfig/Types';
import { constructCurrencyLabel, formatLabelIntoRevenuePriceTitle } from '@utils/currencyUtils/CurrencyUtils';
import {
  ColumnHeaderDisplayNameForPA,
  ColumnHeaderForPA,
  REVENUE,
  REVENUE_SHARE_MARKET_VS_RETAILER,
  TOTAL_REVENUE,
} from '@models/PerformanceAnalysisboard';

export const getStackedChartExportColumn = (data: StackedChartResp[]): TableColumn[] => {
  let segments: any[];
  segments = [{ header: '' }, { header: '' }];
  data.forEach((segment) => {
    segments.push({ header: segment.segment }, { header: segment.segment });
  });
  return segments;
};

const generateBrandKpiValue = (kpiValue: string, volume: string): string => {
  if (kpiValue === REVENUE_SHARE_MARKET_VS_RETAILER) {
    return REVENUE;
  }
  if (
    kpiValue === ColumnHeaderDisplayNameForPA(volume)[ColumnHeaderForPA.marketRetailerVolumeShare]
  ) {
    return `${volume}`;
  }
  return `${kpiValue}`;
};

const generateShareValue = (
  kpiValue: string,
  isRevenueKpi: boolean,
  factRevenueCurrencySymbol: string,
  volume: string,
): string => {
  if (kpiValue === REVENUE_SHARE_MARKET_VS_RETAILER) {
    return TOTAL_REVENUE;
  }
  if (kpiValue === ColumnHeaderDisplayNameForPA(volume)[ColumnHeaderForPA.marketRetailerVolumeShare]) {
    return `Total ${volume}`;
  }
  return isRevenueKpi ? `${kpiValue} (${factRevenueCurrencySymbol})` : `${kpiValue}`;
};

export const getStackedChartExportRow = (
  data: StackedChartResp[],
  kpi: string,
  factRevenue: FactRevenue,
  volume: string,
): DataCell[][] => {
  const valueIndex = 0;
  const formatter = new ExcelFormatter();
  let outputData: any[];
  outputData = [];
  let labelData: string[] = [];
  const kpiValue = formatLabelIntoRevenuePriceTitle(
    getKpiDisplayName(kpi, volume),
    constructCurrencyLabel(factRevenue),
  );
  const isRevenueKpi: boolean = kpiValue.includes('Revenue');
  data.forEach(function (segment) {
    segment.share.series.forEach(function (series, j) {
      const brandKpi = [
        { value: generateBrandKpiValue(kpiValue, volume), type: ColumnType.Text },
        { value: series.label, type: ColumnType.Text },
      ];
      if (outputData[j] === undefined) {
        outputData[j] = [...brandKpi];
        labelData[j] = series.label;
      }
    });
    //load label and value seperately, so that it can load into correct order
    //Will need to load all the label 1st so that it wont trigger value undefined error
    segment.share.series.forEach(function (series) {
      const arrayKey = labelData.indexOf(series.label);
      series.data.forEach((period) => {
        const value = formatKpiForExport(Kpi.revenueShare, { value: period.value } as Fact, formatter)[valueIndex];
        outputData[arrayKey].push({ value: value, type: ColumnType.Percentage });
      });
    });
  });
  //Revenue
  const revenueShareValue = [
    {
      value: generateShareValue(kpiValue, isRevenueKpi, factRevenue.currencySymbol, volume),
      type: ColumnType.Text,
    },
    { value: '', type: ColumnType.Text },
  ];
  //Segment Share
  const segmentShareValue = [
    { value: `Segment Share`, type: ColumnType.Text },
    { value: '', type: ColumnType.Text },
  ];
  data.forEach(function (segment) {
    const firstPeriodValue = formatKpiForExport(
      isRevenueKpi ? Kpi.revenue : Kpi.marketUnits,
      { value: segment.data[0].value } as Fact,
      formatter,
    )[valueIndex];
    const secondPeriodValue = formatKpiForExport(
      isRevenueKpi ? Kpi.revenue : Kpi.marketUnits,
      { value: segment.data[1].value } as Fact,
      formatter,
    )[valueIndex];
    revenueShareValue.push(
      {
        // @ts-ignore
        value: firstPeriodValue,
        type: isRevenueKpi ? ColumnType.Currency : ColumnType.Number,
      },
      {
        value: secondPeriodValue,
        type: isRevenueKpi ? ColumnType.Currency : ColumnType.Number,
      },
    );
    const firstSegmentSharePeriodValue = formatKpiForExport(
      Kpi.revenueShare,
      { value: segment.share.data[0].value } as Fact,
      formatter,
    )[valueIndex];
    const secondSegmentSharePeriodValue = formatKpiForExport(
      Kpi.revenueShare,
      { value: segment.share.data[1].value } as Fact,
      formatter,
    )[valueIndex];
    segmentShareValue.push(
      // @ts-ignore
      { value: firstSegmentSharePeriodValue, type: ColumnType.Percentage },
      { value: secondSegmentSharePeriodValue, type: ColumnType.Percentage },
    );
  });
  outputData.push(revenueShareValue);
  outputData.push(segmentShareValue);
  return outputData;
};
export const getStackedChartSummaryRow = (data: StackedChartResp[]): DataCell[][] => {
  let periods: any[];
  periods = [
    { value: '', type: ColumnType.Text },
    { value: '', type: ColumnType.Text },
  ];
  data.forEach((segment) => {
    periods.push(
      { value: segment.share.data[0].label, type: ColumnType.Text },
      { value: segment.share.data[1].label, type: ColumnType.Text },
    );
  });
  return [periods];
};
